import { Typography } from '@mui/material';
import React, { FC, useCallback, useState } from 'react';

import { TagSchema } from '@sbu/hooks.tags';
import { useTheme } from '@sumologic/yama-ds-components';

import { TagInput, TagInputProps } from 'shared/components/internal/Tags';

import { TagChips } from '../TagChips';
import { getContentTypes } from '../types';
import css from './TagEditor.module.scss';

const isValid = (value: string) =>
  /^[a-zA-Z0-9-_ .:]+$/.test(value) && !/^_/.test(value);

const INVALID_TAG_MESSAGE =
  'Tags must only contain letters, numbers, hyphens, periods, underscores, and spaces.';
const INVALID_TAG_UNDERSCORE_MESSAGE = 'Tags cannot start with an underscore.';

export interface TagEditorProps {
  autocompleteList?: string[];
  className?: string;
  disabled?: boolean;
  errorHint?: string;
  fullWidth?: boolean;
  keepFocus?: boolean;
  label?: string;
  placeholder?: string;
  source?: string;
  tags: string[];
  tagSchemas: TagSchema[];
  updateTagsLoading?: boolean;
  readOnlyTags?: string[];
  onAddTag?: (tag: string) => void;
  onBlur?: TagInputProps['onBlur'];
  onFocus?: () => void;
  onRemoveTag?: (tags: string) => void;
}

export const TagEditor: FC<React.PropsWithChildren<TagEditorProps>> = ({
  autocompleteList,
  className,
  disabled = false,
  errorHint,
  fullWidth = false,
  keepFocus = false,
  label,
  placeholder,
  source,
  tags,
  tagSchemas,
  updateTagsLoading = false,
  readOnlyTags = [],
  onAddTag,
  onBlur,
  onFocus,
  onRemoveTag,
}) => {
  const { palette } = useTheme();

  const contentTypes = getContentTypes(source);

  const [keyInputError, setKeyInputError] = useState(!!errorHint);
  const [keyInputErrorHint, setKeyInputErrorHint] = useState(errorHint);

  const [valueInputError, setValueInputError] = useState(false);
  const [valueInputErrorHint, setValueInputErrorHint] = useState('');

  const onInputChange = useCallback((value: string, isKeyInput: boolean) => {
    setKeyInputError(false);
    setKeyInputErrorHint('');
    setValueInputError(false);
    setValueInputErrorHint('');

    if (value) {
      const inputValue: string = value?.trim() || '';

      if (!isValid(inputValue) || inputValue.startsWith('_')) {
        if (isKeyInput) {
          setKeyInputError(true);
          setKeyInputErrorHint(
            inputValue.startsWith('_')
              ? INVALID_TAG_UNDERSCORE_MESSAGE
              : INVALID_TAG_MESSAGE
          );
        } else {
          setValueInputError(true);
          setValueInputErrorHint(
            inputValue.startsWith('_')
              ? INVALID_TAG_UNDERSCORE_MESSAGE
              : INVALID_TAG_MESSAGE
          );
        }
      }
    }
  }, []);

  return (
    <div className={className} data-sumo-test='tag-editor' onFocus={onFocus}>
      {label && (
        <Typography
          color={
            keyInputError || valueInputError
              ? palette.tokens.YamaTypographyColorError
              : palette.text.secondary
          }
          component='label'
        >
          {label}
        </Typography>
      )}
      {!!tags.length && (
        <div className={css.tagChipsContainer}>
          <TagChips
            contentTypes={contentTypes}
            disabled={updateTagsLoading}
            editable={!disabled}
            readOnlyTags={readOnlyTags}
            removeChip={(tag) => {
              if (onRemoveTag) {
                onRemoveTag(tag);
              }
            }}
            tags={tags}
          />
        </div>
      )}
      <div className={css.tagInputContainer}>
        <TagInput
          autocompleteList={autocompleteList}
          contentTypes={contentTypes}
          disabled={disabled || (!keepFocus && updateTagsLoading)}
          keyInputError={keyInputError}
          keyInputErrorHint={keyInputErrorHint}
          valueInputError={valueInputError}
          valueInputErrorHint={valueInputErrorHint}
          fullWidth={fullWidth}
          loading={updateTagsLoading}
          onBlur={onBlur}
          onInputChange={onInputChange}
          onSetValue={(value: string) => {
            if (onAddTag) {
              onAddTag(value as string);
            }
          }}
          placeholder={placeholder}
          tagSchemas={tagSchemas}
        />
      </div>
    </div>
  );
};
